// @ts-nocheck
/* eslint-disable react/prop-types */
import React from 'react';
import { IconButton as MuiIconButton } from '@mui/material';

// Default props as constants
const DEFAULT_COLOR = 'default';
const DEFAULT_SIZE = 'medium';
const DEFAULT_DISABLED = false;
const DEFAULT_EDGE = false;

const TOOLTIP_TRIGGER_PROPS = [
  'className',
  'id',
  'tabIndex',
  'aria-describedby',
  'onMouseOver',
  'onMouseLeave',
  'onFocus',
  'onBlur',
  'onTouchStart',
  'onTouchEnd',
];

/**
 * SolyticsIconButton - A wrapper around MUI IconButton component
 *
 * Icon buttons are commonly found in app bars and toolbars. They trigger actions
 * and are perfect for toggle buttons.
 *
 * @component
 * @example
 * // Basic icon button
 * <SolyticsIconButton aria-label="delete">
 *   <DeleteIcon />
 * </SolyticsIconButton>
 *
 * @example
 * // Colored icon button with size
 * <SolyticsIconButton color="primary" size="small" aria-label="edit">
 *   <EditIcon />
 * </SolyticsIconButton>
 *
 * @example
 * // With custom styling
 * <SolyticsIconButton
 *   aria-label="settings"
 *   bgcolor="rgba(0, 0, 0, 0.04)"
 *   p={1}
 * >
 *   <SettingsIcon />
 * </SolyticsIconButton>
 */
const SolyticsIconButton = React.forwardRef(
  (
    {
      children,
      color = DEFAULT_COLOR,
      size = DEFAULT_SIZE,
      disabled = DEFAULT_DISABLED,
      edge = DEFAULT_EDGE,
      onClick,
      'aria-label': ariaLabel,
      disableRipple = false,
      className,
      id,
      tabIndex,
      'aria-describedby': ariaDescribedBy,
      onMouseOver,
      onMouseLeave,
      onFocus,
      onBlur,
      onTouchStart,
      onTouchEnd,
      title = '',
      sx,
      ...otherProps
    },
    ref
  ) => {
    // Build props object for MUI IconButton
    const iconButtonProps = {
      ref,
      color,
      size,
      disabled,
      ...(edge && { edge }),
      ...(onClick && { onClick }),
      'aria-label': ariaLabel,
      ...(className && { className }),
      ...(id && { id }),
      ...(tabIndex !== undefined && { tabIndex }),
      ...(ariaDescribedBy && { 'aria-describedby': ariaDescribedBy }),
      ...(onMouseOver && { onMouseOver }),
      ...(onMouseLeave && { onMouseLeave }),
      ...(onFocus && { onFocus }),
      ...(onBlur && { onBlur }),
      ...(onTouchStart && { onTouchStart }),
      ...(onTouchEnd && { onTouchEnd }),
      sx: (theme) => ({
        '&:focus-visible': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: '2px',
        },
        ...(typeof sx === 'function' ? sx(theme) : sx),
      }),
      ...(disableRipple && { disableRipple }),
      ...otherProps,
    };

    return (
      <MuiIconButton {...iconButtonProps} title={title}>
        {children}
      </MuiIconButton>
    );
  }
);

SolyticsIconButton.displayName = 'SolyticsIconButton';

SolyticsIconButton.tooltipTriggerProps = TOOLTIP_TRIGGER_PROPS;

export default SolyticsIconButton;
